// src/context/AuthContext.js

import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../firebase/firebase";
import { isIOSApp } from "../utils/platform";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);

  // ✅ Auth ready (Firebase has resolved the initial session)
  const [authReady, setAuthReady] = useState(false);

  // ✅ Optional cached data (now lazy / on-demand)
  const [profile, setProfile] = useState(null);
  const [billing, setBilling] = useState(null);

  // ✅ Only what we still support
  const [planType, setPlanType] = useState("free"); // "free" | "monthly" (or others you add later)
  const [expiresAt, setExpiresAt] = useState(null);
  const [remainingQuestions, setRemainingQuestions] = useState(0);
  const [chatInputDisabled, setChatInputDisabled] = useState(false);

  const [upgradeMessage, setUpgradeMessage] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [userType, setUserType] = useState(null);

  const clearUserState = useCallback(() => {
    setProfile(null);
    setBilling(null);
    setPlanType("free");
    setExpiresAt(null);
    setRemainingQuestions(0);
    setChatInputDisabled(false);
    setUpgradeMessage(null);
    setShowUpgrade(false);
    setUserType(null);
  }, []);

  const buildUpgradeStateFromBilling = useCallback((billingData) => {
    const {
      plan_type = "free",
      expires_at = null,
      remaining_questions = 0,
    } = billingData || {};

    setPlanType(plan_type);
    setExpiresAt(expires_at);
    setRemainingQuestions(remaining_questions);

    const now = new Date();
    const expires = expires_at ? new Date(expires_at) : null;
    const isIOS = isIOSApp();

    // ✅ Only free + monthly are supported (NO session-based plan)
    const disabled =
      (plan_type === "free" && remaining_questions <= 0) ||
      (plan_type === "monthly" && expires && now >= expires);

    setChatInputDisabled(disabled);

    let message = null;
    let shouldShow = false;

    const billingLink = (
      <span
        style={{ color: "#007bff", cursor: "pointer", textDecoration: "underline" }}
        onClick={() => (window.location.href = "/billing")}
      >
        billing
      </span>
    );

    if (plan_type === "monthly" && expires && now >= expires) {
      message = isIOS
        ? "Your monthly plan has expired. Please upgrade at www.asklawly.com"
        : <>Your monthly plan has expired. Please upgrade at {billingLink}.</>;
      shouldShow = true;
    } else if (plan_type === "free" && remaining_questions <= 0) {
      message = isIOS
        ? "You’ve used all your questions. Please upgrade at www.asklawly.com"
        : <>You’ve used all your questions. Please upgrade at {billingLink}.</>;
      shouldShow = true;
    }

    setUpgradeMessage(message);
    setShowUpgrade(shouldShow);

    // ✅ user_type should come from profile or billing (NOT plan_type)
    const resolvedUserType = billingData?.user_type ?? null;
    setUserType(resolvedUserType);
    console.log("[AuthContext] userType set to:", resolvedUserType);
  }, []);

  // ✅ NEW: fetch profile ONLY when requested (lazy)
  const refreshProfile = useCallback(async () => {
    try {
      if (!BACKEND_URL) return null;
      if (!currentUser) return null;

      const token = await currentUser.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      const profileRes = await axios.get(`${BACKEND_URL}/profile`, { headers });
      const profileData = profileRes.data;

      setProfile(profileData);

      // user_type can also come from profile if present
      const resolvedUserType = profileData?.user_type ?? userType ?? null;
      setUserType(resolvedUserType);

      return profileData;
    } catch (err) {
      console.error("❌ Error fetching profile info:", err);
      return null;
    }
  }, [currentUser, userType]);

  // ✅ NEW: fetch billing ONLY when requested (lazy)
  const refreshBilling = useCallback(async () => {
    try {
      if (!BACKEND_URL) return null;
      if (!currentUser) return null;

      const token = await currentUser.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      const billingRes = await axios.get(`${BACKEND_URL}/user/billing`, { headers });
      const billingData = billingRes.data;

      setBilling(billingData);
      buildUpgradeStateFromBilling(billingData);

      return billingData;
    } catch (err) {
      console.error("❌ Error fetching billing info:", err);
      return null;
    }
  }, [currentUser, buildUpgradeStateFromBilling]);

  // ✅ Optional convenience: refresh both on demand
  const refreshUserData = useCallback(async () => {
    // Run sequentially to reduce burst load (feel free to change to Promise.all later)
    await refreshProfile();
    await refreshBilling();
  }, [refreshProfile, refreshBilling]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);

      if (!user) {
        clearUserState();
      } else {
        // ✅ Lazy-load policy: do NOT auto-fetch profile/billing here.
        // Pages can call refreshProfile/refreshBilling when needed.
        // If you need global gating immediately, call refreshBilling() from Dashboard instead.
      }

      setAuthReady(true);
    });

    return () => unsubscribe();
  }, [clearUserState]);

  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      clearUserState();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        authReady,

        // optional cached data
        profile,
        billing,

        // billing-derived gating
        planType,
        expiresAt,
        remainingQuestions,
        chatInputDisabled,

        upgradeMessage,
        showUpgrade,
        userType,

        // lazy loaders
        refreshProfile,
        refreshBilling,
        refreshUserData,

        logout,
      }}
    >
      {authReady && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
